import { useCallback, useEffect, useRef, useState } from 'react';
import type { SessionUser } from '@/server-threads';
import type { Settings, ThemeChoice } from '@/settings';
import { PersonalSettings } from './personal-settings';
import { CompanyTeam } from './team';
import { CompanyProfile } from './profile';
import { CompanyProcessingSettings } from './processing-settings';
import { CompanyModelSettings } from './model-settings';
import { useCompany } from './context';
import { UsagePage } from '@/usage/usage';
import { CompanyBillingSection } from '@/billing/company-section';
import { SpeechPolicySection } from '@/voice/speech-policy-section';
import { CompanyVoiceSection } from './company-voice-section';
import { UnsavedSettingsDialog } from '@/components/unsaved-settings-dialog';
import {
  isCompanySettingsView,
  isSettingsView,
  parseWorkspaceRoute,
  routeFor,
  type SettingsBeforeCloseDetail,
} from '@/workspace/navigation';
import { settingsMeasure } from '@/workspace/view-definitions';
import { useWorkspace } from '@/workspace/context';
import type { WorkspaceView } from '@/workspace/types';
import { isAppHashChange } from '@/lib/chat-links';
import { DesktopDownloads } from '@/desktop-downloads/desktop-downloads';
import { useAppCopy } from '@/i18n/context';
import { useNamespaceCopy } from '@/i18n/ns';
import companySettingsCopy from '@/i18n/ns/company-settings';

export function CompanySettingsPage({
  user,
  onSaved,
  onAccountDeleted,
  onPasswordChanged,
  onLoggedOutEverywhere,
  beforeBillingRedirect,
  themeChoice,
  onChooseTheme,
}: {
  user: SessionUser | null;
  onSaved: (settings: Settings) => void;
  onAccountDeleted: () => void;
  onPasswordChanged: () => void;
  onLoggedOutEverywhere: () => void;
  beforeBillingRedirect?: () => void;
  /** The saved appearance and the way to choose it (light, dark, system). */
  themeChoice?: ThemeChoice;
  onChooseTheme?: (choice: ThemeChoice) => void | Promise<void>;
}) {
  const { returnToWork } = useWorkspace();
  const appCopy = useAppCopy();
  const companyCopy = useNamespaceCopy(companySettingsCopy);
  const [section, setSection] = useState<WorkspaceView>('account');
  const [editState, setEditState] = useState({ dirty: false, busy: false });
  const [pendingDestination, setPendingDestination] = useState<WorkspaceView | 'close' | null>(
    null,
  );
  const [routeRevision, setRouteRevision] = useState(0);
  const [resumeAfterDiscard, setResumeAfterDiscard] = useState(false);
  const afterDiscard = useRef<(() => void) | undefined>(undefined);
  const updateEditState = useCallback(
    (next: { dirty: boolean; busy: boolean }) => setEditState(next),
    [],
  );
  const { data, loading, error, refresh } = useCompany();
  const contentRef = useRef<HTMLElement>(null);
  const writeSectionUrl = (target: WorkspaceView, mode: 'push' | 'replace' = 'replace') => {
    // Once the browser has moved the history entry (Back/Forward), replacing it
    // would rewrite the entry the user came from and make the next Back skip
    // it; pushing puts the Settings entry back on top of it instead. In-app
    // requests have not moved the URL yet, so replacing keeps the stack as is.
    const url = location.pathname + location.search + routeFor(target);
    if (mode === 'push' && location.hash !== url.slice(url.indexOf('#')))
      history.pushState(null, '', url);
    else history.replaceState(null, '', url);
    setRouteRevision((revision) => revision + 1);
  };
  const completeNavigation = (target: WorkspaceView | 'close') => {
    setEditState({ dirty: false, busy: false });
    setPendingDestination(null);
    // A stored continuation runs once the page is clean: the close path's, or
    // the return to a history entry the browser had already moved to. Without
    // one, return to the workspace location from which Settings was opened.
    if (target === 'close' || afterDiscard.current) setResumeAfterDiscard(true);
    else {
      setSection(target);
      writeSectionUrl(target);
    }
  };
  useEffect(() => {
    const readSection = (event?: Event) => {
      const fromUrl = parseWorkspaceRoute(location.hash).view;
      if (fromUrl !== section && isSettingsView(fromUrl)) {
        if (editState.dirty || editState.busy) {
          // A browser move (Back/Forward, a hash edit) already changed the
          // entry: the page pushes its section back on top of it while it
          // asks, and Discard returns to that entry so its re-fired
          // hashchange lands there on the clean page. The workspace guards
          // its own section changes before writing (runAfterSettingsGuard);
          // an app write that still lands here is rewritten in place.
          const browserMove = event !== undefined && !isAppHashChange(event);
          afterDiscard.current = undefined;
          writeSectionUrl(section, browserMove ? 'push' : 'replace');
          if (!editState.busy) {
            if (browserMove) afterDiscard.current = () => history.back();
            setPendingDestination(fromUrl);
          }
        } else setSection(fromUrl);
      }
    };
    readSection();
    window.addEventListener('hashchange', readSection);
    return () => window.removeEventListener('hashchange', readSection);
  }, [section, editState.dirty, editState.busy]);
  useEffect(() => {
    if (!editState.dirty && !editState.busy) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    const beforeClose = (event: Event) => {
      event.preventDefault();
      const detail = (event as CustomEvent<SettingsBeforeCloseDetail | undefined>).detail;
      writeSectionUrl(section, detail?.navigationAlreadyApplied ? 'push' : 'replace');
      if (!editState.busy) {
        // Every product dispatcher supplies a continuation; a detail-less
        // dispatch (tests, future callers) must not drop one already supplied.
        afterDiscard.current = detail?.afterDiscard ?? afterDiscard.current;
        // An in-app section change asks with the section copy; its
        // continuation pushes the new entry once the page is clean.
        setPendingDestination(detail?.section ?? 'close');
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    window.addEventListener('poneva:settings-before-close', beforeClose);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      window.removeEventListener('poneva:settings-before-close', beforeClose);
    };
  }, [section, editState.dirty, editState.busy]);
  useEffect(() => {
    // Notify the shared menu after React has replaced any old dirty-navigation listener.
    // replaceState alone does not emit hashchange, and dispatching it synchronously can
    // make the previous listener reject a navigation the user has just confirmed.
    // The dispatch is the app's own, not a browser traversal, so it carries the
    // same marker as writeWorkspaceHash.
    if (routeRevision > 0)
      window.dispatchEvent(new CustomEvent('hashchange', { detail: { inApp: true } }));
  }, [routeRevision]);
  useEffect(() => {
    if (!resumeAfterDiscard || editState.dirty || editState.busy) return;
    setResumeAfterDiscard(false);
    const complete = afterDiscard.current ?? returnToWork;
    afterDiscard.current = undefined;
    complete();
  }, [resumeAfterDiscard, editState.dirty, editState.busy, returnToWork]);
  useEffect(() => {
    contentRef.current?.focus();
  }, [section]);
  const companySection = isCompanySettingsView(section);
  return (
    <main
      className="company-settings-page workspace-settings-content"
      data-testid="company-settings-page"
    >
      <div className="company-settings-layout">
        <section
          ref={contentRef}
          tabIndex={-1}
          className="company-settings-content"
          data-measure={settingsMeasure(section)}
          data-testid={`company-settings-${section}`}
        >
          {!user ? (
            <p>{appCopy.loginToUseMemories}</p>
          ) : companySection ? (
            loading ? (
              <p role="status">{appCopy.loadingMemories}</p>
            ) : error ? (
              <>
                <p role="alert">{error}</p>
                <button className="company-secondary" onClick={() => void refresh()}>
                  {appCopy.tryAgain}
                </button>
              </>
            ) : !data?.company ? (
              <>
                <h1>{companyCopy.companyInviteTitle}</h1>
                <p>{companyCopy.companyInviteDescription}</p>
              </>
            ) : section === 'team' ? (
              <CompanyTeam />
            ) : section === 'usage' ? (
              <UsagePage />
            ) : section === 'profile' ? (
              <>
                <CompanyProfile onEditStateChange={updateEditState} />
                {/* BUILD 2: the company's voice and speech switches. */}
                <SpeechPolicySection />
                <CompanyVoiceSection />
              </>
            ) : section === 'processing' ? (
              <CompanyProcessingSettings onEditStateChange={updateEditState} />
            ) : section === 'models' ? (
              <CompanyModelSettings onEditStateChange={updateEditState} />
            ) : (
              <CompanyBillingSection beforeRedirect={beforeBillingRedirect} />
            )
          ) : section === 'desktop' ? (
            <DesktopDownloads />
          ) : (
            <PersonalSettings
              key={section}
              section={section}
              user={user}
              onSaved={onSaved}
              onAccountDeleted={onAccountDeleted}
              onPasswordChanged={onPasswordChanged}
              onLoggedOutEverywhere={onLoggedOutEverywhere}
              onEditStateChange={updateEditState}
              themeChoice={themeChoice}
              onChooseTheme={onChooseTheme}
            />
          )}
        </section>
      </div>
      {pendingDestination !== null && (
        <UnsavedSettingsDialog
          open
          intent={pendingDestination === 'close' ? 'close' : 'back'}
          onCancel={() => {
            afterDiscard.current = undefined;
            setPendingDestination(null);
          }}
          onDiscard={() => pendingDestination && completeNavigation(pendingDestination)}
        />
      )}
    </main>
  );
}
